import React, { useCallback, useState } from "react";
import { Button, Collapse, Divider, Input } from "antd";
import { DownOutlined } from "@ant-design/icons";
import AddIcon from "../../../../../../../../../../../../Components/Icons/addIcon";
import { useAgentsStore } from "../../../../../../../../../../store/agents.store";
import {
    selectDraftProducts,
    selectHiddenFields,
    selectIsScopeDirty,
    selectProducts,
} from "../../../../../../../../../../store/agentDetail/agentDetailSelectors";
import { AGENT_PRODUCTS_SAVE_SCOPE } from "../../../../../../../../../../agents.constants";
import {
    AgentDetailSectionEnum,
    IAgentProductDetails,
} from "../../../../../../../../../../agentDetail.model";
import { useUnsavedChangesGuard } from "../../../../../../../../hooks/useUnsavedChangesGuard";
import { ConfirmationModal } from "../../../../../../../confirmationModal/confirmationModal.index";
import { Footer } from "../../../footer/footer.index";
import { AccordionHeader } from "./components/accordionHeader/accodionHeader.index";
import { AccordionBody } from "./components/accordionBody/accordionBody.index";
import { AddProduct } from "./components/addProduct/addProduct.index";
import {
    getPrefillWritingNumber,
} from "./components/addProduct/addProduct.utils";
import styles from "./productsContracts.module.css";

export const ProductsContracts = () => {
    const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
    const { contractAndLicensingInformation } = useAgentsStore(selectHiddenFields);
    const isAddProductHidden = contractAndLicensingInformation.includes("addProduct");
    const products = useAgentsStore(selectDraftProducts);
    const serverProducts = useAgentsStore(selectProducts);
    const updateDraftField = useAgentsStore((state) => state.updateDraftField);

    const {
        guardNavigation,
        isModalOpen,
        isSaving,
        canSave,
        handleModalCancel,
        handleModalSave,
    } = useUnsavedChangesGuard(AGENT_PRODUCTS_SAVE_SCOPE);

    const openAddProductModal = useCallback(() => {
        setIsAddProductModalOpen(true);
    }, []);

    const handleAddProduct = useCallback(() => {
        guardNavigation(openAddProductModal);
    }, [guardNavigation, openAddProductModal]);

    const handleCloseAddProductModal = useCallback(() => {
        setIsAddProductModalOpen(false);
    }, []);

    const handleSharedFieldChange = useCallback(
        (field: "agentWritingNumber", value: string | null) => {
            const nextValue = value === "" ? null : value;
            const nextProducts = products.map(
                (product): IAgentProductDetails => ({
                    ...product,
                    [field]: nextValue,
                })
            );
            updateDraftField(
                AgentDetailSectionEnum.CONTRACT_AND_LICENSING_INFORMATION,
                "agentProductDetails",
                nextProducts
            );
        },
        [products, updateDraftField]
    );

    const hasProducts = serverProducts.length > 0;
    const agentWritingNumber = getPrefillWritingNumber(products);

    return (
        <div className={styles.outerCtn}>
            <div className={styles.headerCtn}>
                <h2 className={styles.title}>Products & Contracts</h2>
                {!isAddProductHidden && (
                    <Button
                        type="default"
                        className={styles.addProductButton}
                        onClick={handleAddProduct}
                        disabled={isSaving}
                    >
                        <AddIcon width={14} height={14} />
                        <span className={styles.addProductButtonText}>New Product</span>
                    </Button>
                )}
            </div>
            {hasProducts && (
                <div className={styles.inputCtn}>
                    <div className={styles.item}>
                        <div className={styles.label}>Agent Writing Number</div>
                        <div className={styles.value}>
                            <Input
                                className={styles.input}
                                value={agentWritingNumber}
                                placeholder="Enter Agent Writing Number"
                                allowClear
                                disabled={isSaving}
                                onChange={(e) =>
                                    handleSharedFieldChange("agentWritingNumber", e.target.value)
                                }
                            />
                        </div>
                    </div>
                </div>
            )}
            <div className={styles.productsCtn}>
                {hasProducts ? (
                    serverProducts.map((product) => {
                        const draftProduct = products.find((p) => p.productUid === product.productUid);
                        if (!draftProduct) return null;
                        return (
                            <Collapse
                                key={product.productUid}
                                expandIconPlacement="end"
                                expandIcon={({ isActive }) => (
                                    <DownOutlined rotate={isActive ? -180 : 0} />
                                )}
                                className={styles.collapse}
                                items={[
                                    {
                                        classNames: { header: styles.collapseHeader },
                                        key: product.productUid,
                                        label: <AccordionHeader product={product} />,
                                        children: <AccordionBody product={draftProduct} />,
                                    },
                                ]}
                            />
                        );
                    })
                ) : (
                    <div className={styles.noProductsCtn}>
                        <p className={styles.noProductsText}>No products assigned yet</p>
                    </div>
                )}
            </div>
            <Footer saveScope={AGENT_PRODUCTS_SAVE_SCOPE} />
            <Divider className={styles.divider} />

            <AddProduct open={isAddProductModalOpen} onClose={handleCloseAddProductModal} />

            <ConfirmationModal
                open={isModalOpen}
                saving={isSaving}
                onCancel={handleModalCancel}
                onSave={handleModalSave}
                confirmDisabled={!canSave}
            />
        </div>
    );
};
